import { useNavigate } from "react-router-dom";
import { useContext, useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Modal,
  Paper,
  Stack,
  TextField,
} from "@mui/material";
import { Edit, Search } from "@mui/icons-material";
import { Icon } from "@iconify/react";
import Fuse from "fuse.js";
import "./styles.css";
import { makeStyles } from "@mui/styles";
import { createRevenueTargetingModel } from "../../services/RevenueTargetingModels";
import AuthContext from "../../context/AuthContext";
import SnackbarContext from "../../context/SnackbarContext";

const useStyles = makeStyles({
  editButton: {
    visibility: "hidden",
  },
  campaignButton: {
    "&:hover $editButton": {
      visibility: "inherit",
    },
  },
});

const createModalStyles = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 280,
  backgroundColor: "background.paper",
  borderRadius: "6px",
  boxShadow: 24,
  p: 4,
};

const avatarStyle = {
  backgroundColor: "#009cf8",
};

export default function RevenueTargetingModelList({
  revenueTargetingModels,
  selectedRevenueTargetingModelID,
  loading,
  getModels,
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredCampaigns, setFilteredCampaigns] = useState([]);
  const [open, setOpen] = useState(false);
  const [createModelName, setCreateModelName] = useState("");
  const [createBtnLoading, setCreateBtnLoading] = useState(false);

  const auth = useContext(AuthContext);

  const snackbar = useContext(SnackbarContext);

  const classes = useStyles();

  const navigate = useNavigate();

  const handleOpen = () => {
    setCreateModelName("");
    setOpen(true);
  };
  const handleClose = () => setOpen(false);

  useEffect(() => {
    setFilteredCampaigns(
      revenueTargetingModels.map((revenueTargetingModel) => ({
        item: revenueTargetingModel,
      }))
    );
  }, [revenueTargetingModels]);

  const handleClick = (id) => {
    navigate(`/model/${id}`);
  };

  const createBtnDisabled = () => {
    return createModelName.trim().length === 0;
  };

  const handleCreateModel = () => {
    setCreateBtnLoading(true);
    createRevenueTargetingModel(
      auth.token,
      auth.userAccountID,
      createModelName,
      "Prospecting"
    )
      .then((response) => {
        if (response.id) {
          snackbar.createSuccess("New model created!");
          handleClose();
          getModels(true);
        } else {
          throw new Error("Invalid response from API.");
        }
      })
      .catch((error) => {
        snackbar.createError("Error creating Revenue Training Model.");
      })
      .finally(() => {
        setCreateBtnLoading(false);
      });
  };

  const handleChange = (event) => {
    setSearchTerm(event.target.value);

    if (event.target.value.trim() === "") {
      setFilteredCampaigns(
        revenueTargetingModels.map((revenueTargetingModel) => ({
          item: revenueTargetingModel,
        }))
      );
    } else {
      const fuse = new Fuse(revenueTargetingModels, {
        keys: ["name"],
        shouldSort: false,
      });
      setFilteredCampaigns(fuse.search(event.target.value));
    }
  };

  return (
    <>
      <Box
        sx={{ backgroundColor: "neutral.main", padding: "12px 24px 12px 24px" }}
      >
        <Button
          onClick={handleOpen}
          variant="contained"
          color="primary"
          sx={{ width: "100%", fontWeight: "bold" }}
        >
          + Create Model
        </Button>
        <Modal open={open} onClose={handleClose}>
          <Box sx={createModalStyles}>
            <Grid align="center">
              <Avatar style={avatarStyle}>
                <Icon icon="uil:channel-add" />
              </Avatar>
            </Grid>
            <Stack spacing={2}>
              <TextField
                label="Name"
                variant="standard"
                value={createModelName}
                onChange={(event) => setCreateModelName(event.target.value)}
                placeholder="Enter name"
                fullWidth
                required
              />
              <Button
                disabled={createBtnDisabled()}
                onClick={handleCreateModel}
                loading={createBtnLoading}
                type="submit"
                color="primary"
                variant="contained"
                fullWidth
              >
                Create Model
              </Button>
            </Stack>
          </Box>
        </Modal>
        <TextField
          sx={{
            marginTop: "12px",
            marginBottom: "6px",
            width: "100%",
            fontSize: "8px",
          }}
          placeholder="Search Models"
          size="small"
          value={searchTerm}
          variant="outlined"
          onChange={handleChange}
          InputProps={{
            style: { fontSize: ".875rem" },
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      {loading ? (
        <Box
          sx={{
            backgroundColor: "#fdfdfd",
            width: "100%",
            textAlign: "center",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Paper
          style={{ maxHeight: "calc(100vh - 360px)", overflow: "auto" }}
          elevation={0}
        >
          <List sx={{ backgroundColor: "neutral.main", padding: 0 }}>
            {filteredCampaigns.map(({ item }) => (
              <ListItem
                button
                className={classes.campaignButton}
                onClick={() => handleClick(item.id)}
                variant={
                  item.id === selectedRevenueTargetingModelID
                    ? "campaign-list-selected"
                    : !item.active
                    ? "campaign-list-inactive"
                    : "campaign-list-item"
                }
                key={item.id}
                secondaryAction={
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/model/${item.id}/edit`);
                    }}
                    className={classes.editButton}
                    size="small"
                    edge="end"
                    aria-label="edit"
                  >
                    <Edit />
                  </IconButton>
                }
              >
                <ListItemText
                  primaryTypographyProps={{
                    noWrap: true,
                    variant: "campaign-list-item",
                    style: {
                      paddingRight: "24px",
                    },
                  }}
                  primary={item.name}
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
    </>
  );
}
